/**
 * MDM Filters Component
 *
 * Purpose: Search and filter controls for MDM values
 * Architecture: Presentational component for search, scope, and status filtering
 * Dependencies: Material-UI, MDM types
 * Author: Development Team
 * Created: 2025-01-21
 */

import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { ValueListFilters } from '../../types/mdm';

/**
 * MDMFilters Props Interface
 */
export interface MDMFiltersProps {
  /**
   * Current search term
   */
  searchTerm: string;

  /**
   * Handler for search input change
   */
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;

  /**
   * Current filter values
   */
  filters: ValueListFilters;

  /**
   * Handler for filter changes
   */
  onFilterChange: (key: keyof ValueListFilters, value: any) => void;

  /**
   * Optional test ID for automation
   */
  dataTestId?: string;
}

/**
 * MDM Filters Component
 *
 * Purpose: Provides comprehensive filtering controls for MDM values
 *
 * Features:
 * - Search by value name
 * - Filter by scope (GLOBAL, LOCAL, OVERRIDDEN, or ALL)
 * - Toggle include inactive values
 * - Toggle include deleted values
 *
 * @param props - Component props
 * @returns React component
 *
 * @example
 * <MDMFilters
 *   searchTerm="active"
 *   onSearchChange={handleSearchChange}
 *   filters={filters}
 *   onFilterChange={handleFilterChange}
 *   dataTestId="mdm-filters"
 * />
 */
export const MDMFilters: React.FC<MDMFiltersProps> = ({
  searchTerm,
  onSearchChange,
  filters,
  onFilterChange,
  dataTestId,
}) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} data-testid={dataTestId}>
      {/* Search Input */}
      <TextField
        size="small"
        placeholder="Search values..."
        value={searchTerm}
        onChange={onSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ minWidth: 200 }}
        data-testid={`${dataTestId}-search`}
      />

      {/* Scope Filter Dropdown */}
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Scope Filter</InputLabel>
        <Select
          value={filters.scope || 'ALL'}
          onChange={(e) =>
            onFilterChange('scope', e.target.value === 'ALL' ? undefined : e.target.value)
          }
          label="Scope Filter"
          data-testid={`${dataTestId}-scope-filter`}
        >
          <MenuItem value="ALL">All Scopes</MenuItem>
          <MenuItem value="GLOBAL">Global Only</MenuItem>
          <MenuItem value="LOCAL">Local Only</MenuItem>
          <MenuItem value="OVERRIDDEN">Overridden Only</MenuItem>
        </Select>
      </FormControl>

      {/* Include Inactive Switch */}
      <FormControlLabel
        control={
          <Switch
            checked={filters.include_inactive || false}
            onChange={(e) => onFilterChange('include_inactive', e.target.checked)}
            size="small"
            data-testid={`${dataTestId}-include-inactive-switch`}
          />
        }
        label="Include Inactive"
      />

      {/* Include Deleted Switch */}
      <FormControlLabel
        control={
          <Switch
            checked={filters.include_deleted || false}
            onChange={(e) => onFilterChange('include_deleted', e.target.checked)}
            size="small"
            data-testid={`${dataTestId}-include-deleted-switch`}
          />
        }
        label="Include Deleted"
      />
    </Box>
  );
};

export default MDMFilters;
